import { Languages, Locale, TELLOPORT_AVAILABLE_LANGUAGES_COOKIE, getCookie, setCookie } from '@frontend/common';
import { useEffect, useState } from 'react';
import { MessageFormatElement } from 'react-intl';

import * as de from './de.json';
import * as en from './en.json';
import * as fr from './fr.json';
import * as nl from './nl.json';

const AVAILABLE_LANGUAGES_EVENT = `cookie:${TELLOPORT_AVAILABLE_LANGUAGES_COOKIE}`;

type LanguageOption = { value: Locale; displayName: string; shortDisplayName: string };

function getAvailableLanguagesFromCookie(): LanguageOption[] | null {
    const cookie = getCookie(TELLOPORT_AVAILABLE_LANGUAGES_COOKIE);
    if (!cookie) return null;
    try {
        const values = JSON.parse(cookie) as string[];
        const filtered = Languages.filter((l) => values.includes(l.value));
        return filtered.length > 0 ? filtered : null;
    } catch {
        return null;
    }
}

export const useLocale = () => {
    const [messages, changeMessages] = useState<Record<string, string> | Record<string, MessageFormatElement[]>>(en);
    const [locale, setLocale] = useState<Locale>();
    const [availableLanguages, setAvailableLanguages] = useState<LanguageOption[] | null>(getAvailableLanguagesFromCookie);
    const defaultBrowserLanguage = getCorrectLocale(window.navigator.language);

    const listener = () => {
        const availableLangResult = getAvailableLanguagesFromCookie();
        setAvailableLanguages(availableLangResult);

        const cookieLanguage = getCookie('language');
        let newLocale = getCorrectLocale(defaultBrowserLanguage.toString());
        if (cookieLanguage !== undefined) {
            newLocale = getCorrectLocale(cookieLanguage.toString());
        }
        if (availableLangResult && newLocale && !availableLangResult.some((l) => l.value === newLocale)) {
            newLocale = availableLangResult[0].value;
        }

        setLocale(newLocale);
    };

    useEffect(() => {
        listener();
        window.addEventListener('languageChanged', listener);
        window.addEventListener(AVAILABLE_LANGUAGES_EVENT, listener);
        return () => {
            window.removeEventListener('languageChanged', listener);
            window.removeEventListener(AVAILABLE_LANGUAGES_EVENT, listener);
        };
    }, []);

    useEffect(() => {
        if (locale) {
            changeMessages(messagesMap.get(locale));
        }
    }, [locale]);

    const changeLocale = (locale: Locale) => {
        setCookie('language', locale.valueOf(), 365);
        window.dispatchEvent(new CustomEvent('languageChanged'));
    };

    return { messages, locale, changeLocale, availableLanguages } as const;
};
export default useLocale;

export function getCorrectLocale(lang: string): Locale {
    switch (lang) {
        case 'en':
        case 'en-US':
            return Locale.ENGLISH;
        case 'nl-NL':
        case 'nl-BE':
        case 'nl':
        case 'be':
            return Locale.DUTCH;
        case 'fr':
        case 'fr-FR':
        case 'fr-BE':
            return Locale.FRENCH;
        case 'de':
        case 'de-DE':
            return Locale.GERMAN;
        default:
            return Locale.ENGLISH;
    }
}

const messagesMap = new Map<Locale, any>();
messagesMap.set(Locale.ENGLISH, en);
messagesMap.set(Locale.DUTCH, nl);
messagesMap.set(Locale.FRENCH, fr);
messagesMap.set(Locale.GERMAN, de);
